import React, { useEffect, useState } from 'react';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import CompanyHotelDashboardFilter from '@app/model/company_hotel/settings/CompanyHotelDashboardFilter';
import DashboardSelectRoomTypesFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectRoomTypesFilter';
import DashboardSelectPlatformFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectPlatformFilter';
import DashboardSelectIsRefundableFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectIsRefundableFilter';
import DashboardSelectGuestsFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectGuestsFilter';
import DashboardSelectNightsFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectNightsFilter';
import DashboardSelectRatesTypeFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectRatesTypeFilter';
import DashboardSelectProviderFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectProviderFilter';
import DashboardSelectMealFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectMealFilter';
import DashboardSelectRegionFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectRegionFilter';
import {
	DashboardSelectAirportCodeCheckFilter,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectAirportCodeCheckFilter';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import {
	useMarketStateFilterInputTools,
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import _orderBy from 'lodash/orderBy';
import DashboardSelectCalendarMonthFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectCalendarMonthFilter';
import FormButton from '@app/component/form/button/FormButton';
import ResetIcon from '@app/component/icon/ResetIcon';
import { Tooltip } from 'react-tooltip';
import DashboardHeaderManageFiltersComponent
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardHeaderManageFiltersComponent';
import { XMarkIcon } from '@heroicons/react/24/outline';
import CompanyHotelDashboardFilterArrangement
	from '@app/model/company_hotel/settings/CompanyHotelDashboardFilterArrangement';
import DashboardSelectCompetitorTagsFilter
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardSelectCompetitorTagsFilter';
import CalendarFiltersSettings from '@app/model/company_hotel/settings/CalendarFiltersSettings';
import {
	useSaveCompanyHotelCalendarFiltersSettingsMutation,
} from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';


const getFiltersMap = (props: {
	filter: MarketStateFilterInput,
	setFilter: (filter: MarketStateFilterInput) => void
}): Map<CompanyHotelDashboardFilter, any> => new Map([
	[CompanyHotelDashboardFilter.ROOM, () => <DashboardSelectRoomTypesFilter filter={props.filter}
	                                                                         onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.PLATFORM, () => <DashboardSelectPlatformFilter filter={props.filter}
	                                                                            onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.FLEX, () => <DashboardSelectIsRefundableFilter filter={props.filter}
	                                                                            onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.GUESTS, () => <DashboardSelectGuestsFilter filter={props.filter}
	                                                                        onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.NIGHTS, () => <DashboardSelectNightsFilter filter={props.filter}
	                                                                        onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.RATE, () => <DashboardSelectRatesTypeFilter filter={props.filter}
	                                                                         onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.PROVIDER, () => <DashboardSelectProviderFilter filter={props.filter}
	                                                                            onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.MEAL, () => <DashboardSelectMealFilter filter={props.filter}
	                                                                    onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.REGION, () => <DashboardSelectRegionFilter filter={props.filter}
	                                                                        onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.AIRPORT_CODE, () => <DashboardSelectAirportCodeCheckFilter filter={props.filter}
	                                                                                        onFilter={props.setFilter} />],
	[CompanyHotelDashboardFilter.COMPETITOR_TAGS, () => <DashboardSelectCompetitorTagsFilter filter={props.filter}
	                                                                                         onFilter={props.setFilter} />],
]);

const DashboardHeaderFiltersFragment = React.memo(() => {

	const [showSecondFiltersRow, setShowSecondFiltersRow] = useState(false);
	const toaster = useToaster();
	const { t } = useTranslation();
	const { filter, setFilters, resetFilters } = useMarketStateFilterInputTools();
	const props = { filter: filter, setFilter: setFilters };

	const { companyHotel } = useSelectedCompanyHotel();

	const saveCompanyHotelCalendarFiltersMutation = useSaveCompanyHotelCalendarFiltersSettingsMutation({
		onError: (e: string) => {
			toaster.error('Filters', t(e));
		},
	});

	useEffect(() => {
		setShowSecondFiltersRow(companyHotel.settings.calendarFilters.showSecondFiltersRow);
	}, [companyHotel.settings.calendarFilters]);

	const filtersMap = getFiltersMap(props);

	let filtersArrangement = companyHotel.settings.calendarFilters.filtersArrangement;
	filtersArrangement = _orderBy(filtersArrangement, 'position');

	const save = (input: CalendarFiltersSettings) => {
		saveCompanyHotelCalendarFiltersMutation.mutate({ companyHotelId: companyHotel.id, input });
	};

	return <div
		className="bg-white shadow-sm border-t border-gray-200">
		<div className="mx-auto max-w-7xl py-3 px-4 sm:px-6 lg:px-8">
			<div className="md:flex md:items-center md:justify-between gap-x-4">
				<DashboardSelectCalendarMonthFilter />
				<div className="flex flex-wrap items-center gap-x-2 gap-y-2 md:justify-end">
					<RenderFiltersPerRow filtersArrangement={filtersArrangement} filtersMap={filtersMap} row={0} />
					<div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block"></div>
					<div data-tooltip-id="resetFilters">
						<FormButton state="white" extraClassName="text-xs h-8 w-6"
						            onClick={resetFilters}>
								<span>
										<ResetIcon className="h-3 w-3 fill-current flex-shrink-0" />
								</span>
						</FormButton>
						<Tooltip id="resetFilters">Reset Filters</Tooltip>
					</div>
					<div>
						<DashboardHeaderManageFiltersComponent companyHotel={companyHotel} onSave={save} />
					</div>
				</div>
			</div>
		</div>
		{showSecondFiltersRow && <div
			className="bg-gray-50 shadow-sm border-t border-gray-100">
			<div className="mx-auto max-w-7xl py-3 px-4 sm:px-6 lg:px-8">
				<div className="flex flex-wrap items-center gap-x-2 gap-y-2 md:justify-end">
					<div className="flex flex-wrap items-center gap-x-2 gap-y-2">
						<RenderFiltersPerRow filtersArrangement={filtersArrangement} filtersMap={filtersMap} row={1} />
					</div>
					<div className="h-4 w-px bg-gray-300 hidden mx-1 sm:block"></div>
					<div data-tooltip-id="hideRow">
						<FormButton state="white" extraClassName="text-xs h-8 px-2.5"
						            onClick={() => save({ ...companyHotel.settings.calendarFilters, showSecondFiltersRow: false })}>
								<span>
										<XMarkIcon className="h-3 w-3 fill-current flex-shrink-0" />
								</span>
						</FormButton>
						<Tooltip id="hideRow">Hide Row</Tooltip>
					</div>
				</div>
			</div>
		</div>}
	</div>;
});

const RenderFiltersPerRow: React.FunctionComponent<{
	filtersArrangement: CompanyHotelDashboardFilterArrangement[],
	filtersMap: Map<CompanyHotelDashboardFilter, any>,
	row: number
}> = ({ filtersArrangement, filtersMap, row }) => {
	return filtersArrangement.filter(arr => arr.row === row && !arr.isHidden).map(arrangement => {
		const Component = filtersMap.get(arrangement.filter);
		return <Component key={arrangement.filter} />;
	});
};

export default DashboardHeaderFiltersFragment;
